import ModuleLoaderError from './ModuleLoaderError.js';

/**
 * Module that can dynamically load scripts with or without the use of dynamic imports.
 *
 * @property {Logger} _logger
 * @property {string[]} _modules
 */
export default class ModuleLoader {
  /**
   * @param {Logger} logger
   */
  constructor({ logger }) {
    this._logger = logger;
    this._modules = [];
  }

  /**
   * Load javascript from URL either using dynamic imports or it's alternative, IE11 compatible, implementation.
   * Note: Only works for JavaScript! Substitute implementation doesn't support all ES module features,
   * it just loads and executes the linked JS file.
   *
   * Dynamic imports are wrapped in `new Function` construct to avoid webpack compiling them away. This part is not
   * a browser compatibility solution!
   *
   * TODO: remove backwards compatibility substitute if no impacted users are detected
   *
   * @param {string} url The URL address of JS file you want to load.
   * @returns {Promise<void>}
   */
  async loadModule(url) {
    if (ModuleLoader._supportsDynamicImport()) {
      // Avoid import keyword in order to prevent syntax errors in IE.
      // eslint-disable-next-line no-new,no-new-func
      const imp = new Function(
        'url,ModuleLoaderError',
        'import(url).catch((error) => { throw new ModuleLoaderError(url, error.message); })',
      );
      await imp(url, ModuleLoaderError);
    } else if (!this._modules.includes(url)) {
      await ModuleLoader._importSubstitute(url);
      this._modules.push(url);
      this._logger.warning('Using dynamic import substitute.', 'infra-module-loader');
    }
  }

  /**
   * IE11 compatible implementation of dynamic imports.
   * Note: Only works for JavaScript! Substitute implementation doesn't support all ES module features,
   * it just loads and executes the linked JS file.
   *
   * @param {string} url The URL address of JS file you want to load.
   * @returns {Promise<any>}
   * @private
   */
  static _importSubstitute(url) {
    return new Promise((resolve, reject) => {
      fetch(url)
        .then(response => {
          const type = response.headers.get('Content-Type');
          if (type !== 'application/javascript') {
            reject(
              new Error(`ModuleLoader only loads javascript, detected Content-Type was '${type}'.`),
            );
          }
          return response.blob();
        })
        .then(blob => {
          const script = document.createElement('script');
          script.src = URL.createObjectURL(blob);
          script.onload = () => {
            resolve();
          };
          script.onerror = () => {
            reject(new Error(`Failed to load ${url}.`));
          };

          document.head.appendChild(script);
        })
        .catch(reason => {
          reject(new Error(reason));
        });
    });
  }

  /**
   * Checks if the browser running this code supports dynamic imports.
   * Sourced from https://gist.github.com/ebidel/3201b36f59f26525eb606663f7b487d0
   *
   * @returns {boolean}
   * @private
   */
  static _supportsDynamicImport() {
    try {
      // eslint-disable-next-line no-new,no-new-func
      new Function('import("")');
      return true;
    } catch (err) {
      return false;
    }
  }
}
